import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import i18n from "@/i18n"

// List of all wizard view names in order
const wizardViews = [
  'WizardIntro',
  'WizardContact',
  'WizardProjectType',
  'WizardPickupType',
  'WizardDisposal',
  'WizardMap',
  'WizardNotes',
  'WizardLoading',
  'WizardResult',
  'WizardConfirmation'
]

export const useRollOffWizardStore = defineStore('rollOffWizard', () => {
  // Current view in the wizard, starts with the first view
  const currentView = ref(wizardViews[0])
  
  // Validation state
  const introFormValid = ref(null);

  // Computed property to get the current step index based on the current view
  const currentStep = computed(() => wizardViews.indexOf(currentView.value))

  // Additional state variables for the wizard
  const canada = ref(false)
  const reCaptcha = ref(false)
  const newstoreapi = ref(false)
  const lang = ref('en')
  const country = ref('us')
  const loading = ref(false)

  // Modal 
  const showWarning = ref(false)
  const showAllOptions = ref(false)

  // Additional state variables to be send to the API
  const projectType = ref(null);
  const pickupType = ref(null);
  const selectedDisposal = ref([]);
  const mapLocation = ref(null);
  const placement = ref('')
  const accessibility = ref('')
  const space = ref('')
  const details = ref('')
  const selectedProduct = ref(null);
  const districtProducts = ref([]);

  const products = [
    {
      title: "10 Yard Dumpster",
      size: "14 ft. L x 7.5 ft. x 3.5 ft.",
      image: "https://img.wasteconnections.com/corporate-office-the-woodlands/request_quote/construction-service/10_Yard_Person.png",
      descrition: "Heavy Loads",
      descrition2: "(stay within weight limit)",
      material: "Roofing, Stumps, Dirt, Concrete, Brick",
      icons : ["mdi-home", "mdi-axe", "mdi-shovel", "mdi-wall"],
      titleKey: "rollOffList.10YardDumpster",
      sizeKey: "rollOffWizard.products.10Yard.size",
      descritionKey: "rollOffWizard.products.10Yard.descrition",
      descrition2Key: "rollOffWizard.products.10Yard.descrition2",
      materialKey: "rollOffWizard.products.10Yard.material"
    },
    {
      title: "15 Yard Dumpster",
      size: "16 ft. L x 7.5 ft. x 4.5 ft.",
      image: "https://img.wasteconnections.com/corporate-office-the-woodlands/request_quote/construction-service/15_Yard_Person.png",
      descrition: "Home Cleanouts",
      descrition2: "(fits in most driveways)",
      material: "Furniture, Debris, etc.",
      icons : ["mdi-sofa-single", "mdi-package-variant-closed"],
      titleKey: "rollOffList.15YardDumpster",
      sizeKey: "rollOffWizard.products.15Yard.size",
      descritionKey: "rollOffWizard.products.15Yard.descrition",
      descrition2Key: "rollOffWizard.products.15Yard.descrition2",
      materialKey: "rollOffWizard.products.15Yard.material"
    },
    {
      title: "20 Yard Dumpster",
      size: "22 ft. x 7.5 ft. x 4.5 ft.",
      image: "https://img.wasteconnections.com/corporate-office-the-woodlands/request_quote/construction-service/20_Yard_Person.png",
      descrition: "Roofing & Landscaping",
      descrition2: "(fits in most driveways)",
      material: "Leaves, Weeds, Brush, Slate, Asphalt",
      icons : ["mdi-leaf", "mdi-wheel-barrow", "mdi-washing-machine"],
      titleKey: "rollOffList.20YardDumpster",
      sizeKey: "rollOffWizard.products.20Yard.size",
      descritionKey: "rollOffWizard.products.20Yard.descrition",
      descrition2Key: "rollOffWizard.products.20Yard.descrition2",
      materialKey: "rollOffWizard.products.20Yard.material"
    },
    {
      title: "30 Yard Dumpster",
      size: "22 ft. L x 7.5 ft. x 4.5 ft.",
      image: "https://img.wasteconnections.com/corporate-office-the-woodlands/request_quote/construction-service/30_Yard_Person.png",
      descrition: "Remodeling",
      descrition2: "(higher walls)",
      material: "Home remodeling or Contractors",
      icons : ["mdi-hand-saw", "mdi-format-paint"],
      titleKey: "rollOffList.30YardDumpster",
      sizeKey: "rollOffWizard.products.30Yard.size",
      descritionKey: "rollOffWizard.products.30Yard.descrition",
      descrition2Key: "rollOffWizard.products.30Yard.descrition2",
      materialKey: "rollOffWizard.products.30Yard.material"
    },
    {
      title: "40 Yard Dumpster",
      size: "22 ft. L x 7.5 ft. x 8 ft.",
      image: "https://img.wasteconnections.com/corporate-office-the-woodlands/request_quote/construction-service/40_Yard_Person.png",
      descrition: "Construction",
      descrition2: "(residential or commercial)",
      material: "Large amounts of construction debris",
      icons : ["mdi-crane", "mdi-dump-truck", "mdi-wall"],
      titleKey: "rollOffList.40YardDumpster",
      sizeKey: "rollOffWizard.products.40Yard.size",
      descritionKey: "rollOffWizard.products.40Yard.descrition",
      descrition2Key: "rollOffWizard.products.40Yard.descrition2",
      materialKey: "rollOffWizard.products.40Yard.material"
    }
  ];

  const rollOffList = [
      {
          qty: 0 ,
          size:{
              text: i18n.t('rollOffList.10YardDumpster', 'en'),
              textFr: i18n.t('rollOffList.10YardDumpster', 'fr'),
              binSize: "10 YARD",
              spec: i18n.t('rollOffList.10YardDumpsterSpec', 'en'),
              specFr: i18n.t('rollOffList.10YardDumpsterSpec', 'fr'),
              img: "https://img.wasteconnections.com/corporate-office-the-woodlands/request_quote/construction-service/10_Yard_Person.png",
              description: i18n.t('rollOffList.10YardDumpsterDesc', 'en'),
              descriptionFr: i18n.t('rollOffList.10YardDumpsterDesc', 'fr'),
              icon1: "house",
              icon2: "axe",
              icon3: "shovel",
              icon4: "brickwall",
              recommended: false,
              available: false,
          },
          frequency: {},
      },
      {
          qty: 0 ,
          size:{
              text: i18n.t('rollOffList.15YardDumpster', 'en'),
              textFr: i18n.t('rollOffList.15YardDumpster', 'fr'),
              binSize: "15 YARD",
              spec: i18n.t('rollOffList.15YardDumpsterSpec', 'en'),
              specFr: i18n.t('rollOffList.15YardDumpsterSpec', 'fr'),
              img: "https://img.wasteconnections.com/corporate-office-the-woodlands/request_quote/construction-service/15_Yard_Person.png",
              description: i18n.t('rollOffList.15YardDumpsterDesc', 'en'),
              descriptionFr: i18n.t('rollOffList.15YardDumpsterDesc', 'fr'),
              icon1: "couch",
              icon2: "box",
              icon3: "",
              icon4: "",
              recommended: false,
              available: false,
          },
          frequency: {},
      },
      {
          qty: 0 ,
          size:{
              text: i18n.t('rollOffList.20YardDumpster', 'en'),
              textFr: i18n.t('rollOffList.20YardDumpster', 'fr'),
              binSize: "20 YARD",
              spec: i18n.t('rollOffList.20YardDumpsterSpec', 'en'),
              specFr: i18n.t('rollOffList.20YardDumpsterSpec', 'fr'),
              img: "https://img.wasteconnections.com/corporate-office-the-woodlands/request_quote/construction-service/20_Yard_Person.png",
              description: i18n.t('rollOffList.20YardDumpsterDesc', 'en'),
              descriptionFr: i18n.t('rollOffList.20YardDumpsterDesc', 'fr'),
              icon1: "leaf",
              icon2: "",
              icon3: "wheelbarrow",
              icon4: "cementmixer",
              recommended: false,
              available: false,
          },
          frequency: {},
      },
      {
          qty: 0 ,
          size:{
              text: i18n.t('rollOffList.30YardDumpster', 'en'),
              textFr: i18n.t('rollOffList.30YardDumpster', 'fr'),
              binSize: "30 YARD",
              spec: i18n.t('rollOffList.30YardDumpsterSpec', 'en'),
              specFr: i18n.t('rollOffList.30YardDumpsterSpec', 'fr'),
              img: "https://img.wasteconnections.com/corporate-office-the-woodlands/request_quote/construction-service/30_Yard_Person.png",
              description: i18n.t('rollOffList.30YardDumpsterDesc', 'en'),
              descriptionFr: i18n.t('rollOffList.30YardDumpsterDesc', 'fr'),
              icon1: "gardenshovel",
              icon2: "saw",
              icon3: "paintroller",
              icon4: "constructionhelmet",
              recommended: false,
              available: false,
          },
          frequency: {},
      },
      {
          qty: 0,
          size:{
              text: i18n.t('rollOffList.40YardDumpster', 'en'),
              textFr: i18n.t('rollOffList.40YardDumpster', 'fr'),
              binSize: "40 YARD",
              spec: i18n.t('rollOffList.40YardDumpsterSpec', 'en'),
              specFr: i18n.t('rollOffList.40YardDumpsterSpec', 'fr'),
              img: "https://img.wasteconnections.com/corporate-office-the-woodlands/request_quote/construction-service/40_Yard_Person.png",
              description: i18n.t('rollOffList.40YardDumpsterDesc', 'en'),
              descriptionFr: i18n.t('rollOffList.40YardDumpsterDesc', 'fr'),
              icon1: "liftmachine",
              icon2: "wheelloader",
              icon3: "brickwall",
              icon4: "",
              recommended: false,
              available: false,
          },
          frequency: {},
      },
  ];

  function setProjectType(type) {
    projectType.value = type;
  }

  function setPickupType(type) {
    pickupType.value = type;
  }

  function setMapLocation({ lat, lng }) {
    const url = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=18&size=800x600&maptype=satellite&markers=color:red%7C${lat},${lng}&key=${process.env.VUE_APP_GMID}`;
    mapLocation.value = url;
  }

  function setPlacement(val) {
    placement.value = val
  }

  function setAccessibility(val) {
    accessibility.value = val
  }

  function setSpace(val) {
    space.value = val
  }
  
  function setDetails(val) {
    details.value = val
  }

  function setRecommendedProduct() {
    const product = recommendDumpster();
    selectedProduct.value = product;
  }

  function selectProductByIndex(index) {
    selectedProduct.value = districtProducts.value[index];
  }

  function recommendDumpster() {

    if(projectType.value == "long-term") {
      // recommend the largest container (priority 40, 30, 20, 15, 10)
      return districtProducts.value[districtProducts.value.length-1]
    }
    else if(projectType.value == "short-term"){
      // recommend smallest available (priority 10, 15, 20, 30, 40)
      return districtProducts.value[0]
    }
    else if(projectType.value == "recurring"){
      //  recommend 20 or higher if they exist, otherwise largest (priority 20, 30, 40, 15, 10)
      const priorityOrder = [products[2], products[3], products[4], products[1], products[0]]
      for (let i = 0; i < priorityOrder.length; i++) {
        if (districtProducts.value.includes(priorityOrder[i])) {
          return priorityOrder[i]
        }
      }
    }
    else {
      // Fallback: default to something in the middle of whats avialable
      const index = Math.floor(districtProducts.value.length / 2)
      return districtProducts.value[index]
    }
  }

  function findRollOffEquivalent(product) {
    if (!product) return null;
    // Try to match by bin size (e.g., "10 YARD", "15 YARD", etc.)
    const binSize = product.title.match(/\d+\s*Yard/i)?.[0]?.toUpperCase();
    if (!binSize) return null;
    return rollOffList.find(item => item.size.binSize === binSize) || null;
  }

  // set available products based on the API data
  function setAvailableProducts(serviceData) {
    districtProducts.value = [];

    // Early return if no serviceData
    if (!Array.isArray(serviceData) || serviceData.length === 0) {
      districtProducts.value = [...products];
      return;
    }

    // Early return if any item does not have TEMP ROLL OFF in LineOfBusiness
    const allHaveTempRollOff = serviceData.every(
      item => item.data?.LineOfBusiness?.toLowerCase().includes('temp roll off')
    );
    if (!allHaveTempRollOff) {
      districtProducts.value = [...products];
      return;
    }

    // Match known sizes
    // Missing 12 and 18 yard sizes, so we will not include them
    serviceData.forEach(item => {
      const name = item.data?.name?.toLowerCase() || '';
      if (name.includes('10 yard') || name.includes('10 cubic yard')) {
        districtProducts.value.push(products[0]);
      } else if (name.includes('15 yard') || name.includes('15 cubic yard')) {
        districtProducts.value.push(products[1]);
      } else if (name.includes('20 yard') || name.includes('20 cubic yard')) {
        districtProducts.value.push(products[2]);
      } else if (name.includes('30 yard') || name.includes('30 cubic yard')) {
        districtProducts.value.push(products[3]);
      } else if (name.includes('40 yard') || name.includes('40 cubic yard')) {
        districtProducts.value.push(products[4]);
      }
    });

    // Remove duplicates and sort
    districtProducts.value = [...new Set(districtProducts.value)].sort(
      (a, b) => products.indexOf(a) - products.indexOf(b)
    );

    // If nothing matched, return all products
    if (districtProducts.value.length === 0) {
      districtProducts.value = [...products];
    }
  }

  function toggleShowAllOptions() {
    showAllOptions.value = !showAllOptions.value;
    if (!showAllOptions.value) {
      setRecommendedProduct();
    }
  }

  function nextView() {
    const idx = wizardViews.indexOf(currentView.value)
    if (idx < wizardViews.length - 1) {
      currentView.value = wizardViews[idx + 1]
    }
  }

  function prevView() {
    const idx = wizardViews.indexOf(currentView.value)
    if (idx > 0) {
      currentView.value = wizardViews[idx - 1]
    }
  }

  function setView(viewName) {
    if (wizardViews.includes(viewName)) {
      currentView.value = viewName
    }
  }

  function resetWizard() {
    currentView.value = wizardViews[0];
    introFormValid.value = null;
    canada.value = false;
    reCaptcha.value = false;
    newstoreapi.value = false;
    country.value = 'us';
    showWarning.value = false;
    showAllOptions.value = false;
    projectType.value = null;
    pickupType.value = null;
    selectedDisposal.value = ref([]);
    mapLocation.value = null;
    placement.value = '';
    accessibility.value = '';
    space.value = '';
    details.value = '';
    selectedProduct.value = null;
    districtProducts.value = ref([]);
  }

  function resetAfterProjectType(){
    pickupType.value = null;
    selectedDisposal.value = ref([]);
    placement.value = '';
    accessibility.value = '';
    space.value = '';
    details.value = '';
    selectedProduct.value = null;
  }

  return {
    currentView,
    introFormValid,
    canada,
    reCaptcha,
    newstoreapi,
    lang,
    country,
    wizardViews,
    showWarning,
    showAllOptions,
    projectType,
    setProjectType,
    pickupType,
    setPickupType,
    selectedDisposal,
    mapLocation,
    setMapLocation,
    placement,
    setPlacement,
    accessibility,
    setAccessibility,
    space,
    setSpace,
    details,
    products,
    rollOffList,
    setDetails,
    selectedProduct,
    setRecommendedProduct,
    selectProductByIndex,
    recommendDumpster,
    findRollOffEquivalent,
    toggleShowAllOptions,
    nextView,
    prevView,
    setView,
    resetWizard,
    currentStep,
    loading,
    setAvailableProducts,
    districtProducts,
    resetAfterProjectType
  }
})
